import { useState, useEffect, useCallback } from 'react';
import { 
  Palette, Layout, Users, Grid3X3, Menu, Layers, Download, Upload, Save, 
  RotateCcw, Eye, Check, AlertCircle, RefreshCw, Copy, Trash2, Settings,
  ChevronRight, X, Sparkles, Monitor, Smartphone, Tablet, ArrowLeftRight,
  HelpCircle, Keyboard
} from 'lucide-react';
import { TemplateGallery, LayoutSelector, ComponentVariantSelector, LayoutType, ColorPicker, ThemeWizard, WizardToggle, ThemeComparison, FuturisticLayoutGallery } from '../../components/theming';
import { ImmersiveThemeGallery } from '../../components/theming/ImmersiveThemeGallery';
import { FuturisticLayoutType } from '../../types/layout.types';
import { MenuConfigurator } from '../../components/theming/MenuConfigurator';
import { WidgetConfigurator } from '../../components/theming/WidgetConfigurator';
import { ThemePreviewEngine } from '../../components/theming/ThemePreviewEngine';
import { useThemeTemplates, ThemeTemplate } from '../../hooks/useThemeTemplates';
import { useUserVariants, UserType, useCreateUserVariant, useUpdateUserVariant, useDeleteUserVariant } from '../../hooks/useUserVariants';
import { useTheme, TenantTheme, ColorScale } from '../../hooks/useTheme';
import { useMenuConfig, useSaveMenuConfig, useResetMenuConfig } from '../../hooks/useMenuConfig';
import { useAvailableWidgets, useSaveWidgetConfig, useResetWidgetConfig, useWidgetConfig } from '../../hooks/useWidgets';
import { useAuthStore } from '../../stores/auth.store';
import { useQuery } from '@tanstack/react-query';
import api from '../../lib/api';

type TabType = 'templates' | 'layout' | 'futuristic' | 'immersive' | 'variants' | 'widgets' | 'menu' | 'components' | 'colors' | 'preview';
type PreviewDevice = 'desktop' | 'tablet' | 'mobile';

const tabs: { id: TabType; label: string; icon: React.ReactNode }[] = [
  { id: 'templates', label: 'Templates', icon: <Palette className="w-4 h-4" /> },
  { id: 'layout', label: 'Layout Básico', icon: <Layout className="w-4 h-4" /> },
  { id: 'futuristic', label: 'Layouts Futuristas', icon: <Sparkles className="w-4 h-4" /> },
  { id: 'immersive', label: 'Temas Imersivos', icon: <Layers className="w-4 h-4" /> },
  { id: 'colors', label: 'Cores', icon: <Palette className="w-4 h-4" /> },
  { id: 'components', label: 'Componentes', icon: <Layers className="w-4 h-4" /> },
  { id: 'variants', label: 'Variantes', icon: <Users className="w-4 h-4" /> },
  { id: 'widgets', label: 'Widgets', icon: <Grid3X3 className="w-4 h-4" /> },
  { id: 'menu', label: 'Menu', icon: <Menu className="w-4 h-4" /> },
  { id: 'preview', label: 'Preview', icon: <Eye className="w-4 h-4" /> },
];

const userTypes: { value: UserType; label: string; description: string }[] = [
  { value: 'ADMIN', label: 'Administrador', description: 'Gestão completa do tenant' },
  { value: 'OPERATOR', label: 'Operador', description: 'Operações do dia-a-dia' },
  { value: 'COMPLIANCE', label: 'Compliance', description: 'Auditoria e conformidade' },
  { value: 'CUSTOMER', label: 'Correntista', description: 'Cliente pessoa física/jurídica' },
  { value: 'EMPLOYEE', label: 'Funcionário', description: 'Colaborador do tenant' },
  { value: 'SUPPLIER', label: 'Fornecedor', description: 'Parceiro comercial' },
];


// Default color scales
const DEFAULT_PRIMARY: ColorScale = {
  '50': '236 254 255', '100': '207 250 254', '200': '165 243 252',
  '300': '103 232 249', '400': '34 211 238', '500': '6 182 212',
  '600': '8 145 178', '700': '14 116 144', '800': '21 94 117',
  '900': '22 78 99', '950': '8 51 68',
};

const DEFAULT_SURFACE: ColorScale = {
  '50': '248 250 252', '100': '241 245 249', '200': '226 232 240',
  '300': '203 213 225', '400': '148 163 184', '500': '100 116 139',
  '600': '71 85 105', '700': '51 65 85', '800': '30 41 59',
  '900': '15 23 42', '950': '2 6 23',
};

interface ThemeState {
  layoutType: LayoutType;
  buttonVariant: string;
  cardVariant: string;
  tableVariant: string;
  primaryColors: ColorScale;
  accentColors: ColorScale;
  surfaceColors: ColorScale;
  fontFamily: string;
  borderRadius: string;
}

// Notification Toast Component
function Toast({ message, type, onClose }: { message: string; type: 'success' | 'error' | 'info'; onClose: () => void }) {
  useEffect(() => {
    const timer = setTimeout(onClose, 4000);
    return () => clearTimeout(timer);
  }, [onClose]);

  const bgColor = type === 'success' ? 'bg-green-600' : type === 'error' ? 'bg-red-600' : 'bg-blue-600';
  const Icon = type === 'success' ? Check : type === 'error' ? AlertCircle : Sparkles;

  return (
    <div className={`fixed bottom-4 right-4 ${bgColor} text-white px-4 py-3 rounded-lg shadow-xl flex items-center gap-3 z-50 animate-slide-up`}>
      <Icon className="w-5 h-5" />
      <span>{message}</span>
      <button onClick={onClose} className="ml-2 hover:opacity-80">
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}

// Confirmation Modal
function ConfirmModal({ 
  isOpen, 
  title, 
  message, 
  confirmLabel = 'Confirmar',
  cancelLabel = 'Cancelar',
  onConfirm, 
  onCancel,
  variant = 'primary'
}: { 
  isOpen: boolean; 
  title: string; 
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  onConfirm: () => void; 
  onCancel: () => void;
  variant?: 'primary' | 'danger';
}) {
  if (!isOpen) return null;

  const confirmBg = variant === 'danger' ? 'bg-red-600 hover:bg-red-500' : 'bg-primary-600 hover:bg-primary-500';

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50">
      <div className="bg-surface-800 rounded-xl p-6 max-w-md w-full mx-4 border border-surface-700">
        <h3 className="text-lg font-semibold text-white mb-2">{title}</h3>
        <p className="text-surface-300 mb-6">{message}</p>
        <div className="flex gap-3 justify-end">
          <button
            onClick={onCancel}
            className="px-4 py-2 bg-surface-700 text-white rounded-lg hover:bg-surface-600 transition-colors"
          >
            {cancelLabel}
          </button>
          <button
            onClick={onConfirm}
            className={`px-4 py-2 ${confirmBg} text-white rounded-lg transition-colors`}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}


// Preview Panel Component
function PreviewPanel({ 
  themeState, 
  selectedTemplate,
  previewDevice,
  setPreviewDevice 
}: { 
  themeState: ThemeState;
  selectedTemplate: ThemeTemplate | null;
  previewDevice: PreviewDevice;
  setPreviewDevice: (device: PreviewDevice) => void;
}) {
  const deviceWidths: Record<PreviewDevice, string> = {
    desktop: 'w-full',
    tablet: 'w-[768px]',
    mobile: 'w-[375px]',
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-white">Preview em Tempo Real</h3>
        <div className="flex items-center gap-2 bg-surface-800 rounded-lg p-1">
          <button
            onClick={() => setPreviewDevice('desktop')}
            className={`p-2 rounded ${previewDevice === 'desktop' ? 'bg-primary-600 text-white' : 'text-surface-400 hover:text-white'}`}
            title="Desktop"
          >
            <Monitor className="w-4 h-4" />
          </button>
          <button
            onClick={() => setPreviewDevice('tablet')}
            className={`p-2 rounded ${previewDevice === 'tablet' ? 'bg-primary-600 text-white' : 'text-surface-400 hover:text-white'}`}
            title="Tablet"
          >
            <Tablet className="w-4 h-4" />
          </button>
          <button
            onClick={() => setPreviewDevice('mobile')}
            className={`p-2 rounded ${previewDevice === 'mobile' ? 'bg-primary-600 text-white' : 'text-surface-400 hover:text-white'}`}
            title="Mobile"
          >
            <Smartphone className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div className={`mx-auto transition-all duration-300 ${deviceWidths[previewDevice]}`}>
        <ThemePreviewEngine
          theme={{
            primaryColors: themeState.primaryColors,
            accentColors: themeState.accentColors,
            surfaceColors: themeState.surfaceColors,
            layoutType: themeState.layoutType,
            buttonVariant: themeState.buttonVariant as any,
            cardVariant: themeState.cardVariant as any,
            tableVariant: themeState.tableVariant as any,
            fontFamily: themeState.fontFamily,
            borderRadius: themeState.borderRadius,
          }}
          mode="dark"
        />
      </div>

      {selectedTemplate && (
        <div className="mt-4 p-4 bg-surface-800 rounded-xl border border-surface-700">
          <div className="flex items-center gap-4">
            <div
              className="w-12 h-12 rounded-lg flex-shrink-0"
              style={{
                background: `linear-gradient(135deg, rgb(${selectedTemplate.primaryColors['500']}), rgb(${selectedTemplate.primaryColors['700']}))`,
              }}
            />
            <div className="flex-1 min-w-0">
              <h4 className="font-medium text-white truncate">{selectedTemplate.name}</h4>
              <p className="text-sm text-surface-400 truncate">{selectedTemplate.description}</p>
            </div>
            <div className="flex gap-2 flex-shrink-0">
              <span className="text-xs bg-surface-700 text-surface-300 px-2 py-1 rounded">
                {selectedTemplate.category}
              </span>
              <span className="text-xs bg-surface-700 text-surface-300 px-2 py-1 rounded">
                {selectedTemplate.layoutType}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


// Tenant Selector Component
function TenantSelector({ 
  selectedTenantId, 
  onSelect 
}: { 
  selectedTenantId: string; 
  onSelect: (id: string) => void;
}) {
  const user = useAuthStore((state) => state.user);
  const isSuperAdmin = user?.isSuperAdmin;

  // Fetch tenants list for super admin - endpoint correto é /super-admin/tenants
  const { data: tenants, isLoading, error } = useQuery({
    queryKey: ['tenants-list'],
    queryFn: async () => {
      const response = await api.get('/super-admin/tenants');
      return response.data;
    },
    enabled: isSuperAdmin,
  });

  // If not super admin, use current tenant
  useEffect(() => {
    if (!isSuperAdmin && user?.tenantId && !selectedTenantId) {
      onSelect(user.tenantId);
    }
  }, [isSuperAdmin, user?.tenantId, selectedTenantId, onSelect]);

  if (!isSuperAdmin) {
    return (
      <div className="p-4 bg-surface-800 rounded-xl border border-surface-700">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-primary-500/20 flex items-center justify-center">
            <Settings className="w-5 h-5 text-primary-400" />
          </div>
          <div>
            <div className="text-sm text-surface-400">Tenant Atual</div>
            <div className="font-medium text-white">{user?.tenantName || user?.tenantId}</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 bg-surface-800 rounded-xl border border-surface-700">
      <label className="block text-sm font-medium text-surface-400 mb-2">
        Selecione o Tenant
      </label>
      {isLoading ? (
        <div className="h-10 bg-surface-700 rounded-lg animate-pulse" />
      ) : error ? (
        <div className="text-red-400 text-sm flex items-center gap-2">
          <AlertCircle className="w-4 h-4" />
          Erro ao carregar tenants. Verifique se você tem permissão de Super Admin.
        </div>
      ) : (
        <select
          value={selectedTenantId}
          onChange={(e) => onSelect(e.target.value)}
          className="w-full px-4 py-2 bg-surface-700 border border-surface-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
        >
          <option value="">Selecione um tenant...</option>
          {tenants?.map((tenant: any) => (
            <option key={tenant.id} value={tenant.id}>
              {tenant.name} {tenant.cnpj ? `(${tenant.cnpj})` : ''}
            </option>
          ))}
        </select>
      )}
    </div>
  );
}


// User Variant Editor Component
function UserVariantEditor({
  tenantId,
  selectedUserType,
  onUserTypeChange,
  themeState,
  onThemeStateChange,
}: {
  tenantId: string;
  selectedUserType: UserType;
  onUserTypeChange: (type: UserType) => void;
  themeState: ThemeState;
  onThemeStateChange: (state: Partial<ThemeState>) => void;
}) {
  const { data: variants, isLoading } = useUserVariants(tenantId);
  const createVariant = useCreateUserVariant();
  const updateVariant = useUpdateUserVariant();
  const deleteVariant = useDeleteUserVariant();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const currentVariant = variants?.find((v) => v.userType === selectedUserType);

  const handleCreateVariant = async () => {
    try {
      await createVariant.mutateAsync({
        tenantId,
        data: {
          userType: selectedUserType,
          layoutType: themeState.layoutType,
          buttonVariant: themeState.buttonVariant,
          cardVariant: themeState.cardVariant,
          tableVariant: themeState.tableVariant,
        },
      });
    } catch (error) {
      console.error('Failed to create variant:', error);
    }
  };

  const handleUpdateVariant = async () => {
    if (!currentVariant) return;
    try {
      await updateVariant.mutateAsync({
        tenantId,
        variantId: currentVariant.id,
        data: {
          layoutType: themeState.layoutType,
          buttonVariant: themeState.buttonVariant,
          cardVariant: themeState.cardVariant,
          tableVariant: themeState.tableVariant,
        },
      });
    } catch (error) {
      console.error('Failed to update variant:', error);
    }
  };

  const handleDeleteVariant = async () => {
    if (!currentVariant) return;
    try {
      await deleteVariant.mutateAsync({
        tenantId,
        variantId: currentVariant.id,
      });
      setShowDeleteConfirm(false);
    } catch (error) {
      console.error('Failed to delete variant:', error);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-white">Variantes por Tipo de Usuário</h3>
          <p className="text-sm text-surface-400">
            Configure aparência diferente para cada tipo de usuário
          </p>
        </div>
        <select
          value={selectedUserType}
          onChange={(e) => onUserTypeChange(e.target.value as UserType)}
          className="px-4 py-2 bg-surface-700 border border-surface-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
        >
          {userTypes.map((type) => (
            <option key={type.value} value={type.value}>
              {type.label}
            </option>
          ))}
        </select>
      </div>

      {/* User Type Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {userTypes.map((type) => {
          const variant = variants?.find((v) => v.userType === type.value);
          const isSelected = selectedUserType === type.value;
          return (
            <button
              key={type.value}
              onClick={() => onUserTypeChange(type.value)}
              className={`p-4 rounded-xl border-2 transition-all text-left ${
                isSelected
                  ? 'border-primary-500 bg-primary-500/10'
                  : variant
                    ? 'border-green-500/50 bg-green-500/5 hover:border-green-500'
                    : 'border-surface-700 bg-surface-800 hover:border-surface-600'
              }`}
            >
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-medium text-white">{type.label}</h4>
                {variant && (
                  <span className="text-xs bg-green-500/20 text-green-400 px-2 py-1 rounded-full flex items-center gap-1">
                    <Check className="w-3 h-3" />
                    Configurado
                  </span>
                )}
              </div>
              <p className="text-sm text-surface-400 mb-2">{type.description}</p>
              {variant && (
                <div className="text-xs text-surface-500">
                  Layout: {variant.layoutType || 'Padrão'}
                </div>
              )}
            </button>
          );
        })}
      </div>


      {/* Variant Configuration */}
      <div className="bg-surface-800 rounded-xl p-6 border border-surface-700">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h4 className="font-medium text-white">
              Configuração para {userTypes.find(t => t.value === selectedUserType)?.label}
            </h4>
            <p className="text-sm text-surface-400">
              {currentVariant ? 'Variante existente - edite as configurações' : 'Nenhuma variante - usando configuração padrão'}
            </p>
          </div>
          <div className="flex gap-2">
            {currentVariant ? (
              <>
                <button
                  onClick={handleUpdateVariant}
                  disabled={updateVariant.isPending}
                  className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-500 disabled:opacity-50 transition-colors"
                >
                  {updateVariant.isPending ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  Salvar Variante
                </button>
                <button
                  onClick={() => setShowDeleteConfirm(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-red-600/20 text-red-400 rounded-lg hover:bg-red-600/30 transition-colors"
                >
                  <Trash2 className="w-4 h-4" />
                  Excluir
                </button>
              </>
            ) : (
              <button
                onClick={handleCreateVariant}
                disabled={createVariant.isPending}
                className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-500 disabled:opacity-50 transition-colors"
              >
                {createVariant.isPending ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                Criar Variante
              </button>
            )}
          </div>
        </div>

        {/* Layout Override */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-surface-300 mb-2">
            Layout Override
          </label>
          <div className="grid grid-cols-4 gap-3">
            {(['SIDEBAR', 'TOPBAR', 'MINIMAL', 'DASHBOARD_FIRST'] as LayoutType[]).map((layout) => (
              <button
                key={layout}
                onClick={() => onThemeStateChange({ layoutType: layout })}
                className={`p-3 rounded-lg border transition-all ${
                  themeState.layoutType === layout
                    ? 'border-primary-500 bg-primary-500/10 text-primary-400'
                    : 'border-surface-600 bg-surface-700 text-surface-300 hover:border-surface-500'
                }`}
              >
                {layout}
              </button>
            ))}
          </div>
        </div>

        {/* Component Variants */}
        <div className="grid grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-surface-300 mb-2">Botões</label>
            <select
              value={themeState.buttonVariant}
              onChange={(e) => onThemeStateChange({ buttonVariant: e.target.value })}
              className="w-full px-3 py-2 bg-surface-700 border border-surface-600 rounded-lg text-white"
            >
              <option value="solid">Solid</option>
              <option value="outline">Outline</option>
              <option value="ghost">Ghost</option>
              <option value="gradient">Gradient</option>
              <option value="glass">Glass</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-surface-300 mb-2">Cards</label>
            <select
              value={themeState.cardVariant}
              onChange={(e) => onThemeStateChange({ cardVariant: e.target.value })}
              className="w-full px-3 py-2 bg-surface-700 border border-surface-600 rounded-lg text-white"
            >
              <option value="flat">Flat</option>
              <option value="elevated">Elevated</option>
              <option value="bordered">Bordered</option>
              <option value="glass">Glass</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-surface-300 mb-2">Tabelas</label>
            <select
              value={themeState.tableVariant}
              onChange={(e) => onThemeStateChange({ tableVariant: e.target.value })}
              className="w-full px-3 py-2 bg-surface-700 border border-surface-600 rounded-lg text-white"
            >
              <option value="striped">Striped</option>
              <option value="bordered">Bordered</option>
              <option value="minimal">Minimal</option>
              <option value="modern">Modern</option>
            </select>
          </div>
        </div>
      </div>

      <ConfirmModal
        isOpen={showDeleteConfirm}
        title="Excluir Variante"
        message={`Tem certeza que deseja excluir a variante para ${userTypes.find(t => t.value === selectedUserType)?.label}? Esta ação não pode ser desfeita.`}
        confirmLabel="Excluir"
        variant="danger"
        onConfirm={handleDeleteVariant}
        onCancel={() => setShowDeleteConfirm(false)}
      />
    </div>
  );
}


// Main Component
export function ThemeEditorAdvanced() {
  const user = useAuthStore((state) => state.user);
  const [activeTab, setActiveTab] = useState<TabType>('templates');
  const [selectedTenantId, setSelectedTenantId] = useState<string>(user?.tenantId || '');
  const [selectedUserType, setSelectedUserType] = useState<UserType>('CUSTOMER');
  const [selectedTemplate, setSelectedTemplate] = useState<ThemeTemplate | null>(null);
  const [appliedTemplateId, setAppliedTemplateId] = useState<string | null>(null);
  const [previewDevice, setPreviewDevice] = useState<PreviewDevice>('desktop');
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' | 'info' } | null>(null);
  const [showSaveConfirm, setShowSaveConfirm] = useState(false);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [showWizard, setShowWizard] = useState(false);
  const [showComparison, setShowComparison] = useState(false);
  const [originalTheme, setOriginalTheme] = useState<ThemeState | null>(null);

  // Theme state
  const [themeState, setThemeState] = useState<ThemeState>({
    layoutType: 'SIDEBAR',
    buttonVariant: 'solid',
    cardVariant: 'elevated',
    tableVariant: 'striped',
    primaryColors: DEFAULT_PRIMARY,
    accentColors: DEFAULT_PRIMARY,
    surfaceColors: DEFAULT_SURFACE,
    fontFamily: 'Inter',
    borderRadius: '0.5rem',
  });

  // Hooks
  const { theme, updateTheme, publishTheme, revertTheme, exportThemeEnhanced, importThemeEnhanced } = useTheme(selectedTenantId);
  const { data: menuConfig, isLoading: menuLoading } = useMenuConfig(selectedTenantId, selectedUserType);
  const saveMenuConfig = useSaveMenuConfig();
  const resetMenuConfig = useResetMenuConfig();
  const { data: availableWidgets } = useAvailableWidgets();
  const { data: widgetConfig, isLoading: widgetsLoading } = useWidgetConfig(selectedTenantId, selectedUserType);
  const saveWidgetConfig = useSaveWidgetConfig();
  const resetWidgetConfig = useResetWidgetConfig();

  // Local state for menu and widgets
  const [localMenuConfig, setLocalMenuConfig] = useState(menuConfig);
  const [localWidgets, setLocalWidgets] = useState(widgetConfig?.widgets || []);

  // Sync local state with fetched data
  useEffect(() => {
    if (menuConfig) setLocalMenuConfig(menuConfig);
  }, [menuConfig]);

  useEffect(() => {
    if (widgetConfig?.widgets) setLocalWidgets(widgetConfig.widgets);
  }, [widgetConfig]);

  // Load theme data when tenant changes
  useEffect(() => {
    if (theme) {
      const loadedTheme = {
        layoutType: theme.layoutType || 'SIDEBAR',
        buttonVariant: theme.buttonVariant || 'solid',
        cardVariant: theme.cardVariant || 'elevated',
        tableVariant: theme.tableVariant || 'striped',
        primaryColors: theme.primaryColors || DEFAULT_PRIMARY,
        accentColors: theme.accentColors || DEFAULT_PRIMARY,
        surfaceColors: theme.surfaceColors || DEFAULT_SURFACE,
        fontFamily: theme.fontFamily || 'Inter',
        borderRadius: theme.borderRadius || '0.5rem',
      };
      setThemeState(loadedTheme);
      // Salva o tema original para comparação
      if (!originalTheme) {
        setOriginalTheme(loadedTheme);
      }
    }
  }, [theme]);

  const updateThemeState = useCallback((updates: Partial<ThemeState>) => {
    setThemeState(prev => ({ ...prev, ...updates }));
    setHasUnsavedChanges(true);
  }, []);

  const handleTemplateSelect = (template: ThemeTemplate) => {
    setSelectedTemplate(template);
    updateThemeState({
      layoutType: template.layoutType,
      buttonVariant: template.buttonVariant || 'solid',
      cardVariant: template.cardVariant || 'elevated',
      tableVariant: template.tableVariant || 'striped',
      primaryColors: template.primaryColors as unknown as ColorScale,
      accentColors: template.accentColors as unknown as ColorScale,
      surfaceColors: template.surfaceColors as unknown as ColorScale,
      fontFamily: template.fontFamily || 'Inter',
      borderRadius: template.borderRadius || '0.5rem',
    });
  };

  const handleTemplateApply = async (template: ThemeTemplate) => {
    setAppliedTemplateId(template.id);
    handleTemplateSelect(template);
    setToast({ message: `Template "${template.name}" aplicado com sucesso!`, type: 'success' });
  };


  // Save all changes
  const handleSaveAll = async () => {
    if (!selectedTenantId) {
      setToast({ message: 'Selecione um tenant primeiro', type: 'error' });
      return;
    }

    try {
      // Save theme
      await updateTheme.mutateAsync({
        layoutType: themeState.layoutType,
        buttonVariant: themeState.buttonVariant,
        cardVariant: themeState.cardVariant,
        tableVariant: themeState.tableVariant,
        primaryColors: themeState.primaryColors,
        accentColors: themeState.accentColors,
        surfaceColors: themeState.surfaceColors,
        fontFamily: themeState.fontFamily,
        borderRadius: themeState.borderRadius,
      });

      // Save menu config if changed
      if (localMenuConfig) {
        await saveMenuConfig.mutateAsync({
          tenantId: selectedTenantId,
          userType: selectedUserType,
          config: localMenuConfig,
        });
      }

      // Save widget config if changed
      if (localWidgets.length > 0) {
        await saveWidgetConfig.mutateAsync({
          tenantId: selectedTenantId,
          userType: selectedUserType,
          config: localWidgets,
        });
      }

      setHasUnsavedChanges(false);
      setShowSaveConfirm(false);
      setToast({ message: 'Todas as alterações foram salvas com sucesso!', type: 'success' });
    } catch (error) {
      console.error('Failed to save:', error);
      setToast({ message: 'Erro ao salvar alterações. Tente novamente.', type: 'error' });
    }
  };

  // Publish theme
  const handlePublish = async () => {
    try {
      await publishTheme.mutateAsync();
      setToast({ message: 'Tema publicado com sucesso!', type: 'success' });
    } catch (error) {
      console.error('Failed to publish:', error);
      setToast({ message: 'Erro ao publicar tema.', type: 'error' });
    }
  };

  // Reset to defaults
  const handleReset = async () => {
    try {
      await revertTheme.mutateAsync();
      await resetMenuConfig.mutateAsync({ tenantId: selectedTenantId, userType: selectedUserType });
      await resetWidgetConfig.mutateAsync({ tenantId: selectedTenantId, userType: selectedUserType });
      
      setThemeState({
        layoutType: 'SIDEBAR',
        buttonVariant: 'solid',
        cardVariant: 'elevated',
        tableVariant: 'striped',
        primaryColors: DEFAULT_PRIMARY,
        accentColors: DEFAULT_PRIMARY,
        surfaceColors: DEFAULT_SURFACE,
        fontFamily: 'Inter',
        borderRadius: '0.5rem',
      });
      setSelectedTemplate(null);
      setAppliedTemplateId(null);
      setHasUnsavedChanges(false);
      setShowResetConfirm(false);
      setToast({ message: 'Configurações resetadas para o padrão!', type: 'info' });
    } catch (error) {
      console.error('Failed to reset:', error);
      setToast({ message: 'Erro ao resetar configurações.', type: 'error' });
    }
  };

  // Export theme
  const handleExport = async () => {
    try {
      const result = await exportThemeEnhanced.mutateAsync();
      const blob = new Blob([JSON.stringify(result, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `theme-${selectedTenantId}-${new Date().toISOString().split('T')[0]}.json`;
      a.click();
      URL.revokeObjectURL(url);
      setToast({ message: 'Tema exportado com sucesso!', type: 'success' });
    } catch (error) {
      console.error('Failed to export:', error);
      setToast({ message: 'Erro ao exportar tema.', type: 'error' });
    }
  };

  // Import theme
  const handleImport = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json';
    input.onchange = async (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (!file) return;
      
      try {
        const text = await file.text();
        const config = JSON.parse(text);
        
        await importThemeEnhanced.mutateAsync(config);
        setToast({ message: 'Tema importado com sucesso!', type: 'success' });
      } catch (error) {
        console.error('Failed to import:', error);
        setToast({ message: 'Erro ao importar tema. Verifique o formato do arquivo.', type: 'error' });
      }
    };
    input.click();
  };


  return (
    <div className="min-h-screen bg-surface-950 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-3">
              <Sparkles className="w-7 h-7 text-primary-400" />
              Editor de Temas Avançado
            </h1>
            <p className="text-surface-400 mt-1">
              Personalize a aparência da plataforma com templates, layouts e variantes
            </p>
          </div>
          
          <div className="flex flex-wrap gap-2">
            <WizardToggle showWizard={showWizard} onToggle={() => setShowWizard(!showWizard)} />
            {hasUnsavedChanges && originalTheme && (
              <button
                onClick={() => setShowComparison(true)}
                className="flex items-center gap-2 px-3 py-2 bg-surface-800 text-surface-300 rounded-lg hover:bg-surface-700 transition-colors"
                title="Comparar alterações"
              >
                <ArrowLeftRight className="w-4 h-4" />
                Comparar
              </button>
            )}
            {hasUnsavedChanges && (
              <span className="flex items-center gap-2 px-3 py-2 bg-yellow-500/20 text-yellow-400 rounded-lg text-sm">
                <AlertCircle className="w-4 h-4" />
                Alterações não salvas
              </span>
            )}
            <button
              onClick={() => setShowResetConfirm(true)}
              className="flex items-center gap-2 px-4 py-2 bg-surface-800 text-surface-300 rounded-lg hover:bg-surface-700 transition-colors"
            >
              <RotateCcw className="w-4 h-4" />
              Resetar
            </button>
            <button
              onClick={handleImport}
              className="flex items-center gap-2 px-4 py-2 bg-surface-800 text-white rounded-lg hover:bg-surface-700 transition-colors"
            >
              <Upload className="w-4 h-4" />
              Importar
            </button>
            <button
              onClick={handleExport}
              disabled={!selectedTenantId}
              className="flex items-center gap-2 px-4 py-2 bg-surface-800 text-white rounded-lg hover:bg-surface-700 disabled:opacity-50 transition-colors"
            >
              <Download className="w-4 h-4" />
              Exportar
            </button>
            <button
              onClick={handlePublish}
              disabled={!selectedTenantId || publishTheme.isPending}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-500 disabled:opacity-50 transition-colors"
            >
              {publishTheme.isPending ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
              Publicar
            </button>
            <button
              onClick={() => setShowSaveConfirm(true)}
              disabled={!selectedTenantId || !hasUnsavedChanges}
              className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-500 disabled:opacity-50 transition-colors"
            >
              <Save className="w-4 h-4" />
              Salvar Tudo
            </button>
          </div>
        </div>

        {/* Tenant Selector */}
        <div className="mb-6">
          <TenantSelector
            selectedTenantId={selectedTenantId}
            onSelect={setSelectedTenantId}
          />
        </div>

        {/* Theme Wizard - Modo Guiado */}
        {showWizard && selectedTenantId && (
          <ThemeWizard
            currentStep={activeTab}
            onStepChange={(step) => setActiveTab(step as TabType)}
            onComplete={() => {
              setShowWizard(false);
              setToast({ message: 'Wizard concluído! Não esqueça de salvar suas alterações.', type: 'success' });
            }}
          />
        )}

        {!selectedTenantId ? (
          <div className="bg-surface-900 rounded-xl border border-surface-800 p-12 text-center">
            <Settings className="w-16 h-16 text-surface-600 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-white mb-2">Selecione um Tenant</h3>
            <p className="text-surface-400">
              Escolha um tenant acima para começar a personalizar o tema
            </p>
          </div>
        ) : (
          <>
            {/* Tabs */}
            <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium whitespace-nowrap transition-colors ${
                    activeTab === tab.id
                      ? 'bg-primary-600 text-white'
                      : 'bg-surface-800 text-surface-300 hover:bg-surface-700'
                  }`}
                >
                  {tab.icon}
                  {tab.label}
                </button>
              ))}
            </div>


            {/* Tab Content */}
            <div className="bg-surface-900 rounded-xl border border-surface-800 p-6">
              {activeTab === 'templates' && (
                <TemplateGallery
                  tenantId={selectedTenantId}
                  appliedTemplateId={appliedTemplateId}
                  onTemplateSelect={handleTemplateSelect}
                  onTemplateApply={handleTemplateApply}
                />
              )}

              {activeTab === 'layout' && (
                <LayoutSelector
                  value={themeState.layoutType}
                  onChange={(layout) => updateThemeState({ layoutType: layout })}
                />
              )}

              {activeTab === 'futuristic' && (
                <FuturisticLayoutGallery
                  selectedLayout={themeState.layoutType as FuturisticLayoutType}
                  onSelectLayout={(layout) => updateThemeState({ layoutType: layout as LayoutType })}
                  tenantId={selectedTenantId}
                  showApplyButton={true}
                />
              )}

              {activeTab === 'immersive' && (
                <ImmersiveThemeGallery />
              )}

              {activeTab === 'components' && (
                <ComponentVariantSelector
                  buttonVariant={themeState.buttonVariant}
                  cardVariant={themeState.cardVariant}
                  tableVariant={themeState.tableVariant}
                  onButtonVariantChange={(v) => updateThemeState({ buttonVariant: v })}
                  onCardVariantChange={(v) => updateThemeState({ cardVariant: v })}
                  onTableVariantChange={(v) => updateThemeState({ tableVariant: v })}
                  primaryColor={themeState.primaryColors['500']}
                />
              )}

              {activeTab === 'colors' && (
                <div className="space-y-8">
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-2">Personalização de Cores</h3>
                    <p className="text-surface-400 mb-6">
                      Escolha as cores que definem a identidade visual da sua plataforma. 
                      Selecione uma cor base e a escala completa será gerada automaticamente.
                    </p>
                  </div>
                  
                  <ColorPicker
                    label="Cor Primária"
                    value={themeState.primaryColors}
                    onChange={(colors) => updateThemeState({ primaryColors: colors })}
                  />
                  
                  <ColorPicker
                    label="Cor de Destaque (Accent)"
                    value={themeState.accentColors}
                    onChange={(colors) => updateThemeState({ accentColors: colors })}
                  />
                  
                  <ColorPicker
                    label="Cores de Superfície"
                    value={themeState.surfaceColors}
                    onChange={(colors) => updateThemeState({ surfaceColors: colors })}
                  />

                  <div className="pt-4 border-t border-surface-700">
                    <h4 className="text-sm font-medium text-surface-300 mb-3">Configurações Adicionais</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm text-surface-400 mb-2">Fonte</label>
                        <select
                          value={themeState.fontFamily}
                          onChange={(e) => updateThemeState({ fontFamily: e.target.value })}
                          className="w-full px-3 py-2 bg-surface-800 border border-surface-700 rounded-lg text-white"
                        >
                          <option value="Inter">Inter</option>
                          <option value="Roboto">Roboto</option>
                          <option value="Open Sans">Open Sans</option>
                          <option value="Poppins">Poppins</option>
                          <option value="Montserrat">Montserrat</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm text-surface-400 mb-2">Arredondamento</label>
                        <select
                          value={themeState.borderRadius}
                          onChange={(e) => updateThemeState({ borderRadius: e.target.value })}
                          className="w-full px-3 py-2 bg-surface-800 border border-surface-700 rounded-lg text-white"
                        >
                          <option value="0">Sem arredondamento</option>
                          <option value="0.25rem">Sutil (4px)</option>
                          <option value="0.5rem">Médio (8px)</option>
                          <option value="0.75rem">Grande (12px)</option>
                          <option value="1rem">Extra grande (16px)</option>
                        </select>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'variants' && (
                <UserVariantEditor
                  tenantId={selectedTenantId}
                  selectedUserType={selectedUserType}
                  onUserTypeChange={setSelectedUserType}
                  themeState={themeState}
                  onThemeStateChange={updateThemeState}
                />
              )}

              {activeTab === 'widgets' && (
                <WidgetConfigurator
                  widgets={localWidgets}
                  availableWidgets={availableWidgets?.map(w => ({
                    code: w.code,
                    name: w.name,
                    description: w.description || '',
                    icon: w.icon,
                    defaultSize: w.defaultSize,
                  })) || []}
                  onChange={(widgets) => {
                    setLocalWidgets(widgets);
                    setHasUnsavedChanges(true);
                  }}
                  isLoading={widgetsLoading}
                />
              )}

              {activeTab === 'menu' && (
                <MenuConfigurator
                  config={localMenuConfig || { sections: [], items: [] }}
                  onChange={(config) => {
                    setLocalMenuConfig(config);
                    setHasUnsavedChanges(true);
                  }}
                  userType={selectedUserType}
                  isLoading={menuLoading}
                />
              )}

              {activeTab === 'preview' && (
                <PreviewPanel
                  themeState={themeState}
                  selectedTemplate={selectedTemplate}
                  previewDevice={previewDevice}
                  setPreviewDevice={setPreviewDevice}
                />
              )}
            </div>

            {/* Floating Preview (visible on non-preview tabs) */}
            {activeTab !== 'preview' && (
              <div className="mt-6 p-4 bg-surface-900 rounded-xl border border-surface-800">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-white">Preview Rápido</h3>
                  <button
                    onClick={() => setActiveTab('preview')}
                    className="flex items-center gap-2 text-sm text-primary-400 hover:text-primary-300"
                  >
                    Ver preview completo
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
                <ThemePreviewEngine
                  theme={{
                    primaryColors: themeState.primaryColors,
                    accentColors: themeState.accentColors,
                    surfaceColors: themeState.surfaceColors,
                    layoutType: themeState.layoutType,
                    buttonVariant: themeState.buttonVariant as any,
                    cardVariant: themeState.cardVariant as any,
                    tableVariant: themeState.tableVariant as any,
                    fontFamily: themeState.fontFamily,
                    borderRadius: themeState.borderRadius,
                  }}
                  mode="dark"
                />
              </div>
            )}
          </>
        )}
      </div>

      {/* Modals */}
      <ConfirmModal
        isOpen={showSaveConfirm}
        title="Salvar Alterações"
        message="Deseja salvar todas as alterações feitas no tema, menu e widgets?"
        confirmLabel="Salvar Tudo"
        onConfirm={handleSaveAll}
        onCancel={() => setShowSaveConfirm(false)}
      />

      <ConfirmModal
        isOpen={showResetConfirm}
        title="Resetar Configurações"
        message="Tem certeza que deseja resetar todas as configurações para o padrão? Esta ação não pode ser desfeita."
        confirmLabel="Resetar"
        variant="danger"
        onConfirm={handleReset}
        onCancel={() => setShowResetConfirm(false)}
      />

      {/* Theme Comparison Modal */}
      {showComparison && originalTheme && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-6">
          <div className="w-full max-w-6xl max-h-[90vh] overflow-auto">
            <ThemeComparison
              currentTheme={{
                primaryColors: originalTheme.primaryColors,
                accentColors: originalTheme.accentColors,
                surfaceColors: originalTheme.surfaceColors,
                layoutType: originalTheme.layoutType,
                buttonVariant: originalTheme.buttonVariant,
                cardVariant: originalTheme.cardVariant,
                tableVariant: originalTheme.tableVariant,
                fontFamily: originalTheme.fontFamily,
                borderRadius: originalTheme.borderRadius,
              }}
              newTheme={{
                primaryColors: themeState.primaryColors,
                accentColors: themeState.accentColors,
                surfaceColors: themeState.surfaceColors,
                layoutType: themeState.layoutType,
                buttonVariant: themeState.buttonVariant,
                cardVariant: themeState.cardVariant,
                tableVariant: themeState.tableVariant,
                fontFamily: themeState.fontFamily,
                borderRadius: themeState.borderRadius,
              }}
              onClose={() => setShowComparison(false)}
            />
          </div>
        </div>
      )}

      {/* Toast Notification */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
}

export default ThemeEditorAdvanced;
